import * as React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useState } from "react";
import CustomConfirmation from "src/components/CustomConfirmation";
import GrButton from "src/design-system/components/GrButton";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrIcon from "src/design-system/components/GrIcon";
import icons from "src/design-system/icons";
import { gr } from "src/design-system/tokens";
import AddWorkoutCalender from "../AddWorkoutCalender";

const PHONE = "@media (max-width: 550px)";

// The workout icon as a CSS mask (coloured with currentColor): shown in place of the avatar's "U"
// when an exercise has no photo or its photo does not load.
const workoutIconMask = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${icons.taskWorkout.width || 24} ${
    icons.taskWorkout.height || 24
  }">${icons.taskWorkout.body}</svg>`
)}") center / contain no-repeat`;

// Small round icon button look for the legacy set / exercise icons (MUI SvgIcons with old
// coloured-circle classes, which use !important).
const legacyIconButtonSx = {
  boxSizing: "border-box",
  width: gr.icon.xl,
  height: gr.icon.xl,
  padding: gr.space[1],
  margin: "0 !important",
  borderRadius: gr.radius.pill,
  background: "transparent !important",
  color: `${gr.color.ink2} !important`,
  fill: "currentColor !important",
  opacity: 1,
  cursor: "pointer",
  transition: "background-color .15s ease, color .15s ease",
};

// One look for the legacy workout markup inside (exercise cards, set rows, Sets / History buttons,
// outlined labels), so every block lines up on the same radius, border and spacing.
const workoutContentSx = {
  fontFamily: gr.font.family,
  color: gr.color.ink,
  "& .workout-set-card, & .workout-Superset-card": {
    position: "relative",
    background: `${gr.color.surface} !important`,
    border: `1px solid ${gr.color.line}`,
    borderRadius: gr.radius.tile,
    boxShadow: "none",
    padding: gr.space[4],
    marginBottom: gr.space[3],
  },
  // "Modified": a small tag on the card's top edge (was a rotated red ribbon).
  "& .workout-modified-mark": {
    position: "absolute",
    top: `calc(-1 * ${gr.space[3]})`,
    left: gr.space[4],
    zIndex: 1,
  },
  // Exercise field: the photo (or a workout icon), the name, then info / change icons in one row.
  "& .exercise-label": { display: "flex", alignItems: "center", cursor: "pointer" },
  "& .exercise-label .MuiAvatar-root": {
    flex: "none",
    width: gr.control.sm,
    height: gr.control.sm,
    borderRadius: gr.radius.chip,
  },
  "& .exercise-label .MuiAvatar-root:not(:has(img))": {
    background: gr.color.brandSoft,
    color: "transparent",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      margin: "auto",
      width: gr.icon.md,
      height: gr.icon.md,
      backgroundColor: gr.color.brand,
      mask: workoutIconMask,
      WebkitMask: workoutIconMask,
    },
  },
  "& .label-text": {
    marginLeft: `${gr.space[2]} !important`,
    fontSize: gr.font.size.md,
    fontWeight: gr.font.weight.medium,
    color: gr.color.ink,
  },
  "& .exercise-action > div, & .exercise-action > div > div": {
    display: "flex",
    alignItems: "center",
    gap: gr.space[1],
  },
  "& .exercise-action svg": {
    ...legacyIconButtonSx,
    display: "block",
    color: gr.color.muted,
    "&:hover": { background: gr.color.tint, color: gr.color.brandDark },
  },
  // Set rows: the add / delete set icons sit in a small toolbar on the row's top edge.
  "& .icon-calendar-workout, & .icon-calendar-workout-small-screen": {
    top: `calc(-1 * ${gr.space[3]})`,
    right: gr.space[3],
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: gr.space[1],
    padding: `calc(${gr.space[1]} / 2)`,
    background: gr.color.surface,
    border: `1px solid ${gr.color.line}`,
    borderRadius: gr.radius.pill,
    boxShadow: gr.shadow.card,
  },
  "& .add-icon-calendar-workout, & .del-icon-calendar-workout-working, & .del-icon-calendar-workout-notwork":
    legacyIconButtonSx,
  "& .add-icon-calendar-workout:hover": {
    background: `${gr.color.brandSoft} !important`,
    color: `${gr.color.brandDark} !important`,
  },
  "& .del-icon-calendar-workout-working:hover": {
    background: `${gr.color.dangerSoft} !important`,
    color: `${gr.color.danger} !important`,
  },
  "& .del-icon-calendar-workout-notwork": {
    color: `${gr.color.lineStrong} !important`,
    cursor: "not-allowed",
  },
  // Set fields (reps, weight, rest…): white boxes on the row, control radius, quiet labels.
  "& .workout-card-inset .MuiOutlinedInput-root": {
    borderRadius: gr.radius.control,
    background: gr.color.surface,
    fontFamily: gr.font.family,
  },
  "& .workout-card-inset .MuiOutlinedInput-notchedOutline": { borderColor: gr.color.line },
  "& .workout-card-inset .MuiInputLabel-root": { fontFamily: gr.font.family, color: gr.color.muted },
  // Remove an extra exercise (⊖): quiet until hovered; superset − / + in the brand colour.
  "& .diary-icon-remove": {
    ...legacyIconButtonSx,
    color: `${gr.color.muted} !important`,
    "&:hover": {
      background: `${gr.color.dangerSoft} !important`,
      color: `${gr.color.danger} !important`,
    },
  },
  "& .diary-icon-plus-minus": {
    color: `${gr.color.brandDark} !important`,
    fill: "currentColor !important",
    width: gr.icon.lg,
    height: gr.icon.lg,
  },
  // "Parameters": a small text link like the other card links.
  "& .set-parameters": {
    gap: gr.space[1],
    color: gr.color.brandStrong,
    fontWeight: gr.font.weight.medium,
    "& .MuiTypography-root": {
      fontFamily: gr.font.family,
      fontSize: gr.font.size.sm,
      fontWeight: gr.font.weight.medium,
    },
    "&:hover": { textDecoration: "underline" },
  },
  "& .workout-Superset-card": { borderLeft: `3px solid ${gr.color.brand}` },
  // Exercises inside a block line up with the block header (no extra indent).
  "& .workout-Superset-card .ms-4": { marginLeft: "0 !important" },
  // A block without instructions keeps no empty, padded box.
  "& .custom_description:empty": { display: "none" },
  // The Sets chevron sits still beside its text (the old bounce looked misaligned).
  "& .bounce": { animation: "none" },
  "& .workout-card-inset": {
    // The add / delete set toolbar is placed against the row.
    position: "relative",
    background: `${gr.color.bg} !important`,
    border: `1px solid ${gr.color.line2}`,
    borderRadius: gr.radius.control,
    boxShadow: "none",
  },
  "& .exercise-label-box": {
    borderColor: gr.color.lineStrong,
    borderRadius: gr.radius.control,
    background: gr.color.surface,
    transition: "border-color .15s ease",
    "&:hover": { borderColor: gr.color.brand },
  },
  "& .outer-label": {
    color: gr.color.muted,
    fontSize: gr.font.size.xs,
  },
  "& .small-contained-dialog-button, & .small-contained-dialog-Button": {
    minHeight: gr.control.sm,
    padding: `0 ${gr.space[3]}`,
    gap: gr.space[1],
    borderRadius: gr.radius.chip,
    background: `${gr.color.brandSoft} !important`,
    color: `${gr.color.brandDark} !important`,
    boxShadow: "none",
    fontFamily: gr.font.family,
    transition: "background-color .15s ease, color .15s ease",
    "& .MuiTypography-root": {
      fontFamily: gr.font.family,
      fontSize: gr.font.size.sm,
      fontWeight: gr.font.weight.medium,
    },
    "& svg": { transition: "transform .24s cubic-bezier(0.2, 0, 0, 1)" },
    "&:hover": {
      background: `${gr.color.brand} !important`,
      color: `${gr.color.onBrand} !important`,
      boxShadow: "none",
    },
  },
  // Exercise instructions: one soft panel (label + text) instead of a teal bar over a box.
  "& .custom_description_title": {
    padding: `${gr.space[2]} ${gr.space[3]} 0`,
    background: gr.color.tint,
    border: `1px solid ${gr.color.line}`,
    borderBottom: 0,
    borderRadius: `${gr.radius.control} ${gr.radius.control} 0 0`,
    color: gr.color.brandStrong,
    fontSize: gr.font.size.xs,
    fontWeight: gr.font.weight.semibold,
  },
  "& .custom_description_text": {
    padding: `${gr.space[1]} ${gr.space[3]} ${gr.space[3]}`,
    background: gr.color.tint,
    border: `1px solid ${gr.color.line}`,
    borderTop: 0,
    borderRadius: `0 0 ${gr.radius.control} ${gr.radius.control}`,
    color: gr.color.ink2,
    fontSize: gr.font.size.sm,
  },
};

export default function EditWorkoutPopup({
  open,
  setOpen,
  date,
  programme,
  data,
  reloadList,
  day,
  member_vdot,
  memberRewardData,
  events,
  setEvents,
  setEditWorkputData,
  setSubmitted,
}) {
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const childRef = React.useRef();
  const [modalChangeExe, setModalChangeExe] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const [questionsToSend, setQuestionsToSend] = useState([]);
  const [editShow, setEditShow] = useState([]);

  const handleParentButtonClick = () => {
    childRef.current.childButtonClickUpdate();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAgreeDelete = (value) => {
    setOpenCloseDialog(true);
  };

  const handleDelete = async () => {
    handleClose();
    setOpenCloseDialog(false);
  };
  React.useEffect(() => {
    if (open) {
      setModalChangeExe(false);
      setIsLoading(false);
      const newQuestions = data?.questions || [];
      let que_list = [];
      if (newQuestions && newQuestions.length > 0) {
        que_list = newQuestions.map((item) => {
          return {
            ...item,
            loading: [],
          };
        });
      }
      setQuestionsToSend(que_list);
      setEditShow(newQuestions.map(() => ({ display: false })));
    }
  }, [open]);

  return (
    <div>
      <CustomConfirmation
        openDelete={openCloseDialog}
        setOpenDelete={setOpenCloseDialog}
        title={"Are you sure you want to Close ?"}
        handleAgree={handleDelete}
      />
      <Dialog
        open={Boolean(open)}
        fullScreen={window.innerWidth <= 550 ? true : false}
        fullWidth
        maxWidth={"lg"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        id="show-dialog-on-full"
        BackdropProps={{ sx: { background: gr.color.overlay } }}
        PaperProps={{
          sx: {
            position: "relative",
            // Closer to the screen edges than MUI's 32px, so more of the workout fits without scrolling.
            margin: gr.space[4],
            maxHeight: `calc(100% - 2 * ${gr.space[4]})`,
            borderRadius: gr.radius.card,
            boxShadow: gr.shadow.raised,
            backgroundImage: "none",
            fontFamily: gr.font.family,
            // Phones: full screen (no margin, full height).
            [PHONE]: { borderRadius: 0, margin: 0, maxHeight: "100%" },
          },
        }}
      >
        {/* Unsaved-changes notice: its own row above the workout, only while there are changes. */}
        {modalChangeExe ? (
          <Box
            role="status"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: gr.space[2],
              margin: `${gr.space[4]} ${gr.space[6]} 0`,
              padding: `${gr.space[2]} ${gr.space[3]}`,
              borderRadius: gr.radius.control,
              background: gr.color.lateSoft,
              color: gr.color.late,
              fontSize: gr.font.size.sm,
              fontWeight: gr.font.weight.medium,
              [PHONE]: { margin: `${gr.space[3]} ${gr.space[4]} 0` },
            }}
          >
            <GrIcon icon={icons.alert} size="md" />
            You have unsaved changes. Remember to save them before closing.
          </Box>
        ) : null}

        <DialogContent
          sx={{
            ...workoutContentSx,
            // The content brings its own 12px (container-fluid): 12 + 12 = 24, same as the footer.
            // Its height is what is left in the popup; it scrolls only when the workout is longer.
            padding: `${gr.space[5]} ${gr.space[3]} ${gr.space[2]}`,
            overflowY: "auto",
            [PHONE]: { padding: `${gr.space[4]} ${gr.space[1]}`, height: "auto" },
          }}
        >
          <AddWorkoutCalender
            ref={childRef}
            date={date}
            programme={programme}
            setOpen={setOpen}
            editData={data}
            day={day}
            reloadList={reloadList}
            setIsLoading={setIsLoading}
            setModalChangeExe={setModalChangeExe}
            member_vdot={member_vdot}
            memberRewardData={memberRewardData}
            events={events}
            setEditWorkputData={setEditWorkputData}
            setEvents={setEvents}
            setSubmitted={setSubmitted}
            questionsToSend={questionsToSend}
            setQuestionsToSend={setQuestionsToSend}
            editShow={editShow}
            setEditShow={setEditShow}
            // The close button sits in the workout's title row (next to its video).
            closeButton={
              <GrIconButton
                variant="close"
                aria-label="Close"
                onClick={modalChangeExe ? handleAgreeDelete : handleClose}
              >
                <GrIcon icon={icons.close} />
              </GrIconButton>
            }
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: `${gr.space[3]} ${gr.space[6]}`,
            borderTop: `1px solid ${gr.color.line}`,
            [PHONE]: { padding: `${gr.space[3]} ${gr.space[4]}` },
          }}
        >
          <GrButton onClick={handleParentButtonClick} loading={isloading}>
            Save changes
          </GrButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
